import { configureStore, combineReducers } from "@reduxjs/toolkit";
import userReducer, { setupUserPersistence } from "./userSlice";
import audioSettingsReducer from "./audioSettingsSlice";
import sessionReducer, { setupSessionPersistence } from "./sessionSlice";
import authPreferencesReducer, { setupAuthPreferencesPersistence } from "./authPreferencesSlice";
import experienceReducer, { setupExperiencePersistence } from "./experienceSlice";
import flagsReducer from "./flagsSlice";
import userSettingsReducer, { setupUserSettingsPersistence } from "./userSettingsSlice";

// ---------------------------------------------------------------------------
// Combine all slice reducers into one app-level reducer
// ---------------------------------------------------------------------------
const appReducer = combineReducers({
  user: userReducer,
  audioSettings: audioSettingsReducer,
  session: sessionReducer,
  authPreferences: authPreferencesReducer,
  experience: experienceReducer,
  flags: flagsReducer,
  userSettings: userSettingsReducer,
});

/**
 * rootReducer intercepts special RESET actions:
 * - RESET_APP_STATE: resets everything to initial state
 * - RESET_APP_STATE_PRESERVE_AUTH_PREFS: resets everything except authPreferences
 */
export const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: { type: string }
) => {
  if (action.type === "RESET_APP_STATE") {
    console.log("[rootReducer] RESET_APP_STATE action received");
    return appReducer(undefined, action);
  }

  if (action.type === "RESET_APP_STATE_PRESERVE_AUTH_PREFS") {
    // Reset all slices except preserve authPreferences
    const preservedAuthPrefs = state?.authPreferences;
    const resetState = appReducer(undefined, action);
    return {
      ...resetState,
      authPreferences: preservedAuthPrefs || resetState.authPreferences
    };
  }

  return appReducer(state, action);
};

// ---------------------------------------------------------------------------
// Configure the Redux store
// ---------------------------------------------------------------------------
export const store = configureStore({
  reducer: rootReducer,
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// ---------------------------------------------------------------------------
// Persist session slice, auth preferences, experience, and user settings
// ---------------------------------------------------------------------------
setupSessionPersistence(store);
setupAuthPreferencesPersistence(store);
setupExperiencePersistence(store);
setupUserPersistence(store);
setupUserSettingsPersistence(store);
